import type {
    ChangeEvent,
    CSSProperties,
    FC,
    FormEvent,
    ReactNode
} from 'react'

import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import PageIntro from '@/components/generals/page-intro'
import PixelText from '@/components/motion/pixel-text'

import {
    ArrowRight,
    Check,
    ChevronDown,
    Loader,
    MonitorPlay,
    Plug,
    ShieldCheck
} from 'lucide-react'

import {
    demoLimits,
    demoRoles,
    demoTeamSizes
} from '@/constants/demo'

type FieldName = 'fullName' | 'email' | 'company' | 'role' | 'teamSize' | 'message'

type Values = Record<FieldName, string> & { website: string }

type Status = 'idle' | 'submitting' | 'success' | 'error'

const initialValues: Values = {
    fullName: '',
    email: '',
    company: '',
    role: '',
    teamSize: '',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/

const expectations: { icon: LucideIcon, title: string, body: string }[] = [
    {
        icon: MonitorPlay,
        title: 'A thirty minute walkthrough',
        body: 'We replay a real incident from first alert to verified fix, then answer whatever your team brings.'
    },
    {
        icon: Plug,
        title: 'Your stack, your signals',
        body: 'Bring the tools you run today. Forge connects to most observability, cloud and code platforms in minutes.'
    },
    {
        icon: ShieldCheck,
        title: 'Safe by default',
        body: 'Read-only access to start, and every change Forge proposes goes through your existing review process.'
    }
]

const validate = (values: Values): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {}

    if (values.fullName.trim().length < 2) errors.fullName = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid work email.'
    if (!values.company.trim()) errors.company = 'Please enter your company.'
    if (values.message.length > demoLimits.message) errors.message = `Please keep this under ${demoLimits.message} characters.`

    return errors
}

const fieldClass = (invalid: boolean): string => cn(
    'w-full rounded-lg bg-white px-3.5 text-[15px] text-ink placeholder:text-ink-subtle',
    'shadow-[inset_0_0_0_1px_rgb(14_14_14/0.14)] transition-[box-shadow,background-color] duration-200 ease-out',
    'hover:shadow-[inset_0_0_0_1px_rgb(14_14_14/0.24)]',
    'focus:shadow-[inset_0_0_0_1px_rgb(93_45_230/0.7),0_0_0_4px_rgb(93_45_230/0.12)] focus:outline-none focus-visible:outline-none',
    invalid && 'shadow-[inset_0_0_0_1px_rgb(180_35_24/0.6)] hover:shadow-[inset_0_0_0_1px_rgb(180_35_24/0.75)] focus:shadow-[inset_0_0_0_1px_rgb(180_35_24/0.8),0_0_0_4px_rgb(180_35_24/0.12)]'
)

interface FieldProps {
    id: FieldName
    label: string
    optional?: boolean
    error?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, optional = false, error, children }): ReactNode => {
    return (
        <div className='flex flex-col gap-2'>
            <label htmlFor={id} className='flex items-baseline justify-between text-sm text-ink'>
                {label}

                {optional && (
                    <span className='text-xs text-ink-subtle'>
                        Optional
                    </span>
                )}
            </label>

            {children}

            {error && (
                <p id={`${id}-error`} className='text-[13px] text-issue'>
                    {error}
                </p>
            )}
        </div>
    )
}

const Demo: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(initialValues)
    const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formMessage, setFormMessage] = useState('')
    const [submittedEmail, setSubmittedEmail] = useState('')

    const update = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { name, value } = event.target
        setValues((current) => ({ ...current, [name]: value }))

        if (errors[name as FieldName]) {
            setErrors((current) => {
                const next = { ...current }
                delete next[name as FieldName]
                return next
            })
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        if (status === 'submitting') return

        const found = validate(values)
        setErrors(found)

        if (Object.keys(found).length) {
            setStatus('error')
            setFormMessage('Please check the highlighted fields.')
            const first = Object.keys(found)[0]
            document.getElementById(first)?.focus()
            return
        }

        setStatus('submitting')
        setFormMessage('')

        try {
            const response = await fetch('/api/demo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => ({})) as { ok?: boolean, message?: string, fields?: Partial<Record<FieldName, string>> }

            if (!response.ok || !data.ok) {
                setErrors(data.fields ?? {})
                setStatus('error')
                setFormMessage(data.message ?? 'Something went wrong. Please try again.')
                return
            }

            setSubmittedEmail(values.email.trim())
            setStatus('success')
            setValues(initialValues)
        }
        catch {
            setStatus('error')
            setFormMessage('We could not reach the server. Check your connection and try again.')
        }
    }

    const firstName = values.fullName.trim().split(' ')[0]

    return (
        <Layout>
            <Seo
                title='Book a demo'
                description='See Forge take a real production incident from first alert to verified fix in a thirty minute walkthrough.'
            />

            <section aria-labelledby='demo-heading' className='relative isolate overflow-hidden'>
                <div aria-hidden='true' className='intro-fade pointer-events-none absolute inset-x-0 top-0 -z-10 h-[40rem] bg-[radial-gradient(45%_55%_at_75%_10%,rgb(93_45_230/0.08),transparent_72%),radial-gradient(40%_50%_at_10%_0%,rgb(158_129_240/0.08),transparent_70%)]' />

                <div className='container-page grid gap-12 pb-16 pt-28 sm:pb-24 sm:pt-36 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] lg:gap-16'>
                    <div className='flex flex-col gap-10'>
                        <PageIntro
                            eyebrow='Book a demo'
                            title='See Forge on your own stack.'
                            description='Tell us a little about your team and we will set up a walkthrough built around the systems you actually run.'
                        />

                        <ul className='flex flex-col gap-6'>
                            {expectations.map((item, index) => {
                                const Icon = item.icon

                                return (
                                    <li
                                        key={item.title}
                                        className='intro-rise flex gap-4'
                                        style={{ '--intro-delay': `${620 + index * 90}ms` } as CSSProperties}
                                    >
                                        <span className='grid size-10 flex-none place-items-center rounded-lg bg-violet-wash text-violet shadow-[inset_0_0_0_1px_rgb(93_45_230/0.16)]'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <span className='flex flex-col gap-1'>
                                            <span className='text-[15px] font-medium text-ink'>
                                                {item.title}
                                            </span>

                                            <span className='max-w-md text-sm leading-relaxed text-ink-muted'>
                                                {item.body}
                                            </span>
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div className='intro-settle' style={{ '--intro-delay': '420ms' } as CSSProperties}>
                        <div className='card card-raised p-6 sm:p-8'>
                            {status === 'success' ? (
                                <div className='swap-in flex min-h-[28rem] flex-col items-start justify-center gap-5' role='status'>
                                    <span className='grid size-12 place-items-center rounded-full bg-healthy-bg text-healthy shadow-[inset_0_0_0_1px_rgb(5_56_26/0.2)]'>
                                        <Check className='size-6' aria-hidden='true' />
                                    </span>

                                    <PixelText id='demo-heading' as='h2' mode='load' text='Request received.' className='font-heading text-3xl text-ink' />

                                    <p className='text-pretty text-ink-muted'>
                                        Thanks for reaching out. Someone from the team will reply to
                                        {' '}
                                        <span className='text-ink'>
                                            {submittedEmail}
                                        </span>
                                        {' '}
                                        within one business day to find a time.
                                    </p>

                                    <div className='flex flex-wrap gap-3 pt-2'>
                                        <Link href='/' className='btn btn-secondary'>
                                            Back to home
                                        </Link>

                                        <Link href='/research' className='btn btn-ghost'>
                                            Read the research
                                            <ArrowRight className='btn-arrow size-4' aria-hidden='true' />
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <form noValidate onSubmit={onSubmit} className='flex flex-col gap-5' aria-labelledby='demo-heading'>
                                    <div className='flex flex-col gap-1.5'>
                                        <h2 id='demo-heading' className='text-lg font-medium text-ink'>
                                            {firstName ? `Nice to meet you, ${firstName}.` : 'Request a walkthrough'}
                                        </h2>

                                        <p className='text-sm text-ink-muted'>
                                            It takes about a minute. We reply within one business day.
                                        </p>
                                    </div>

                                    <Field id='fullName' label='Full name' error={errors.fullName}>
                                        <input
                                            id='fullName'
                                            name='fullName'
                                            type='text'
                                            autoComplete='name'
                                            maxLength={demoLimits.name}
                                            value={values.fullName}
                                            onChange={update}
                                            aria-invalid={Boolean(errors.fullName)}
                                            aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                                            className={cn(fieldClass(Boolean(errors.fullName)), 'h-11')}
                                            placeholder='Ada Moreno'
                                        />
                                    </Field>

                                    <div className='grid gap-5 sm:grid-cols-2'>
                                        <Field id='email' label='Work email' error={errors.email}>
                                            <input
                                                id='email'
                                                name='email'
                                                type='email'
                                                inputMode='email'
                                                autoComplete='email'
                                                maxLength={demoLimits.email}
                                                value={values.email}
                                                onChange={update}
                                                aria-invalid={Boolean(errors.email)}
                                                aria-describedby={errors.email ? 'email-error' : undefined}
                                                className={cn(fieldClass(Boolean(errors.email)), 'h-11')}
                                                placeholder='ada@company.com'
                                            />
                                        </Field>

                                        <Field id='company' label='Company' error={errors.company}>
                                            <input
                                                id='company'
                                                name='company'
                                                type='text'
                                                autoComplete='organization'
                                                maxLength={demoLimits.company}
                                                value={values.company}
                                                onChange={update}
                                                aria-invalid={Boolean(errors.company)}
                                                aria-describedby={errors.company ? 'company-error' : undefined}
                                                className={cn(fieldClass(Boolean(errors.company)), 'h-11')}
                                                placeholder='Company name'
                                            />
                                        </Field>
                                    </div>

                                    <div className='grid gap-5 sm:grid-cols-2'>
                                        <Field id='role' label='Your role' optional error={errors.role}>
                                            <span className='relative block'>
                                                <select
                                                    id='role'
                                                    name='role'
                                                    value={values.role}
                                                    onChange={update}
                                                    className={cn(fieldClass(Boolean(errors.role)), 'h-11 appearance-none pr-10', !values.role && 'text-ink-subtle')}
                                                >
                                                    <option value='' className='bg-white text-ink'>
                                                        Choose one
                                                    </option>

                                                    {demoRoles.map((role) => (
                                                        <option key={role} value={role} className='bg-white text-ink'>
                                                            {role}
                                                        </option>
                                                    ))}
                                                </select>

                                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-subtle' aria-hidden='true' />
                                            </span>
                                        </Field>

                                        <Field id='teamSize' label='Engineering team' optional error={errors.teamSize}>
                                            <span className='relative block'>
                                                <select
                                                    id='teamSize'
                                                    name='teamSize'
                                                    value={values.teamSize}
                                                    onChange={update}
                                                    className={cn(fieldClass(Boolean(errors.teamSize)), 'h-11 appearance-none pr-10', !values.teamSize && 'text-ink-subtle')}
                                                >
                                                    <option value='' className='bg-white text-ink'>
                                                        Choose one
                                                    </option>

                                                    {demoTeamSizes.map((size) => (
                                                        <option key={size} value={size} className='bg-white text-ink'>
                                                            {size}
                                                        </option>
                                                    ))}
                                                </select>

                                                <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-subtle' aria-hidden='true' />
                                            </span>
                                        </Field>
                                    </div>

                                    <Field id='message' label='What should we focus on?' optional error={errors.message}>
                                        <textarea
                                            id='message'
                                            name='message'
                                            rows={4}
                                            maxLength={demoLimits.message}
                                            value={values.message}
                                            onChange={update}
                                            aria-invalid={Boolean(errors.message)}
                                            aria-describedby={errors.message ? 'message-error' : undefined}
                                            className={cn(fieldClass(Boolean(errors.message)), 'resize-none py-3 leading-relaxed')}
                                            placeholder='Noisy alerts, slow investigations, rising cloud spend...'
                                        />
                                    </Field>

                                    <div className='absolute -left-[9999px] h-px w-px overflow-hidden' aria-hidden='true'>
                                        <label htmlFor='website'>
                                            Leave this field empty
                                        </label>

                                        <input id='website' name='website' type='text' tabIndex={-1} autoComplete='off' value={values.website} onChange={update} />
                                    </div>

                                    <div className='flex flex-col gap-3 pt-1'>
                                        <button type='submit' className='btn btn-primary btn-lg w-full' disabled={status === 'submitting'}>
                                            {status === 'submitting'
                                                ? (
                                                    <>
                                                        <Loader className='spin size-4' aria-hidden='true' />
                                                        Sending request
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        Request a demo
                                                        <ArrowRight className='btn-arrow -mr-0.5 size-4' aria-hidden='true' />
                                                    </>
                                                )}
                                        </button>

                                        <p aria-live='polite' className={cn('min-h-5 text-center text-[13px]', status === 'error' ? 'text-issue' : 'text-ink-subtle')}>
                                            {status === 'error' ? formMessage : 'We only use your details to set up the demo.'}
                                        </p>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default Demo
